import { useEffect, useState, useRef } from 'react';
import { type OperationVariables } from '@apollo/react-common';
// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions
import { useSubscription } from '@apollo/react-hooks';
import { type GraphQLTaggedNode, useRelayEnvironment } from 'react-relay';
import { requestSubscription, type Disposable, type OperationType } from 'relay-runtime';
import type { DocumentNode, FormattedExecutionResult } from 'graphql';
import type ApolloClient from 'apollo-client';

import { isRelayEnabled } from './isRelayEnabled.ts';

import { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import { toDocumentNode } from './toDocumentNode.ts';
import { useMemoObj } from './useMemoObj.ts';

export type UseSubscriptionInteropSupportedResult<TData> = {
	data?: TData;
	loading: boolean;
	error?: ApolloErrorInterop;
};

export type SubscriptionDataCallback<TData> = (options: {
	subscriptionData: {
		data?: TData;
		loading: boolean;
		extensions?: Record<string, any>;
	};
	extensions?: Record<string, any>;
}) => void;

export type UseSubscriptionInteropSupportedHookOptions<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = {
	variables?: TVariables;
	skip?: boolean;
	onSubscriptionData?: SubscriptionDataCallback<TData>;
	onSubscriptionComplete?: () => void;
	/**
	 * Operation-level GraphQL `extensions` (e.g. AGG WebSocket replay metadata produced
	 * by `useSubscriptionValues`). Forwarded to the wire payload's `extensions` field via:
	 *   - Apollo build: smuggled as `variables.__extensions` and re-attached to the
	 *     operation context by `AttachExtensionsLink` (Apollo v2's `useSubscription` does
	 *     not forward a per-call `context` option, so the variable hack is still needed
	 *     here — but it stays inside this hook so consumers don't need to know).
	 *   - Relay build: forwarded via `cacheConfig.metadata.extensions`, which
	 *     `RelayWebSocketSubscriptionHandler` reads when building the WS payload.
	 */
	extensions?: Record<string, any>;
	/**
	 * Optional Apollo client to use for subscriptions.
	 * Only used in Apollo mode (non-Relay builds).
	 * In Relay mode, this option is ignored as subscriptions use the Relay environment.
	 */
	client?: ApolloClient<object>;
	/**
	 * Ensures that the interop in Apollo build variant uses the AGG client.
	 * Only used in Apollo mode (non-Relay builds).
	 * In Relay mode, this option is ignored as subscriptions use the Relay environment.
	 */
	interopApolloAggClient?: boolean;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

export type SubscriptionNode = GraphQLTaggedNode | DocumentNode;

declare global {
	interface Window {
		__TEST_ONLY_FORCE_RELAY_MODE__?: boolean;
		GLOBAL_APOLLO_AGG_CLIENT?: ApolloClient<object>;
		MOCK_GATEWAY_APOLLO_CLIENT?: ApolloClient<object>;
	}
}

type ApolloInteropSubscriptionVariables = OperationVariables & {
	__extensions?: Record<string, any>;
};

type SubscriptionExecutionResult<TData> = FormattedExecutionResult<TData, Record<string, any>>;

type SubscriptionResponse<TData> = TData | SubscriptionExecutionResult<TData>;

type SubscriptionOperation<TData, TVariables extends Record<string, any>> = OperationType & {
	readonly variables: TVariables;
	readonly response: SubscriptionResponse<TData>;
};

const isRelayGraphQLTaggedNode = (
	subscription: SubscriptionNode,
): subscription is GraphQLTaggedNode =>
	typeof subscription === 'function' || subscription.kind !== 'Document';

const isFormattedExecutionResult = <TData>(
	response: SubscriptionResponse<TData>,
): response is SubscriptionExecutionResult<TData> =>
	Boolean(response && typeof response === 'object' && ('data' in response || 'errors' in response));

const isSubscriptionData = <TData>(response: SubscriptionResponse<TData>): response is TData =>
	!isFormattedExecutionResult(response);

const getSubscriptionDataAndExtensions = <TData>(
	response: SubscriptionResponse<TData> | null | undefined,
) => {
	if (response == null) {
		return {
			data: undefined,
			extensions: undefined,
		};
	}

	if (isSubscriptionData(response)) {
		return {
			data: response,
			extensions: undefined,
		};
	}

	return {
		data: response.data ?? undefined,
		extensions: response.extensions,
	};
};

export const __TEST_ONLY_getSubscriptionDataCallbackOptions = <TData>(
	response: SubscriptionResponse<TData> | null | undefined,
) => {
	const { data, extensions } = getSubscriptionDataAndExtensions<TData>(response);

	return {
		...(extensions ? { extensions } : {}),
		subscriptionData: {
			data,
			loading: false,
			...(extensions ? { extensions } : {}),
		},
	};
};

const __TEST_ONLY_isTestOnlySafeEnvironment = (): boolean => {
	const isProduction = process.env.NODE_ENV === 'production';
	const isCloudProduction = process.env.CLOUD_ENV === 'production';
	const isCloudStaging = process.env.CLOUD_ENV === 'staging';

	if (isProduction || isCloudProduction || isCloudStaging) {
		return false;
	}

	const isTest = process.env.NODE_ENV === 'test';
	const isDevelopment = process.env.NODE_ENV === 'development';
	const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';

	return isTest || isDevelopment || isLocalhost;
};

export const __TEST_ONLY_setForceRelayMode = (forceRelay: boolean): boolean => {
	if (!__TEST_ONLY_isTestOnlySafeEnvironment()) {
		return false;
	}

	window.__TEST_ONLY_FORCE_RELAY_MODE__ = forceRelay;
	return true;
};

export function useSubscriptionInterop<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	subscription: SubscriptionNode,
	options?: UseSubscriptionInteropSupportedHookOptions<TData, TVariables>,
): UseSubscriptionInteropSupportedResult<TData> {
	const forceRelayMode = window.__TEST_ONLY_FORCE_RELAY_MODE__;
	const shouldUseRelay = forceRelayMode || isRelayEnabled(options?.__callerPackage__);

	if (shouldUseRelay) {
		if (!isRelayGraphQLTaggedNode(subscription)) {
			throw new Error('[Relay migration] useSubscriptionInterop Relay mode requires graphql tag');
		}

		// eslint-disable-next-line react-hooks/rules-of-hooks
		return useSubscriptionInteropRelay<TData, TVariables>(subscription, options);
	} else {
		const { interopApolloAggClient, extensions, ...apolloOptions } = options ?? {};

		// Apollo v2's `useSubscription` does not forward a per-call `context` option down
		// to the WebSocket link, so we smuggle `extensions` through `variables.__extensions`.
		// `AttachExtensionsLink` strips it back out and re-attaches it to the operation
		// context, which `GraphQLWSLink.request` then sets on the wire payload.
		const variables: ApolloInteropSubscriptionVariables | undefined =
			extensions !== undefined
				? {
						...(apolloOptions.variables ?? {}),
						__extensions: extensions,
					}
				: apolloOptions.variables;

		// eslint-disable-next-line react-hooks/rules-of-hooks
		return useSubscription<TData, ApolloInteropSubscriptionVariables>(
			toDocumentNode(subscription),
			{
				...apolloOptions,
				variables,
				...(interopApolloAggClient
					? {
							client: window.GLOBAL_APOLLO_AGG_CLIENT ?? window.MOCK_GATEWAY_APOLLO_CLIENT,
						}
					: {}),
			},
		);
	}
}

const toApolloErrorInterop = (error: unknown): ApolloErrorInterop =>
	new ApolloErrorInterop({
		networkError: error instanceof Error ? error : new Error(String(error)),
	});

function useSubscriptionInteropRelay<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	subscription: GraphQLTaggedNode,
	options?: UseSubscriptionInteropSupportedHookOptions<TData, TVariables>,
): UseSubscriptionInteropSupportedResult<TData> {
	const environment = useRelayEnvironment();
	const [data, setData] = useState<TData | undefined>(undefined);
	const [error, setError] = useState<ApolloErrorInterop | undefined>(undefined);
	const [loading, setLoading] = useState(!options?.skip);
	const disposableRef = useRef<Disposable | null>(null);

	const callbacksRef = useRef(options);
	callbacksRef.current = options;

	const skip = options?.skip;
	const variables = useMemoObj<TVariables>(options?.variables, options?.__callerPackage__);
	const extensions = useMemoObj<Record<string, any>>(
		options?.extensions,
		options?.__callerPackage__,
	);
	useEffect(() => {
		if (skip) {
			setLoading(false);
			return;
		}

		setLoading(true);
		setError(undefined);
		setData(undefined);

		try {
			disposableRef.current = requestSubscription<SubscriptionOperation<TData, TVariables>>(
				environment,
				{
					subscription,
					variables,
					// `RelayWebSocketSubscriptionHandler` reads `cacheConfig.metadata.extensions`
					// and attaches them to the WS payload's `extensions` field.
					// cacheConfig is always set because useMemoObj coerces undefined → {}, so any
					// guard on `extensions` would always evaluate the same way. The consumer
					// (RelayWebSocketSubscriptionHandler) ignores an empty extensions object.
					cacheConfig: { metadata: { extensions } },
					onNext: (response) => {
						const { data: responseData } = getSubscriptionDataAndExtensions<TData>(response);
						setData(responseData);
						setError(undefined);
						setLoading(false);
						callbacksRef.current?.onSubscriptionData?.(
							__TEST_ONLY_getSubscriptionDataCallbackOptions<TData>(response),
						);
					},
					onError: (err: Error) => {
						setError(toApolloErrorInterop(err));
						setLoading(false);
					},
					onCompleted: () => {
						setLoading(false);
						callbacksRef.current?.onSubscriptionComplete?.();
					},
				},
			);
		} catch (err) {
			setError(toApolloErrorInterop(err));
			setLoading(false);
		}

		return () => {
			if (!disposableRef.current) return;
			disposableRef.current.dispose();
			disposableRef.current = null;
		};
	}, [environment, subscription, variables, extensions, skip]);

	return {
		data,
		loading,
		error,
	};
}
